import { useCallback, useMemo, useState } from "react";
import {
  Dialog,
  DialogPopup,
  DialogTitle,
  useIsMobile,
  type Omittable,
} from "@salestrak/ui";
import { getDefaultValueByType } from "@salestrak/custom-fields";
import { AddCustomFieldsContent } from "#app/components/custom-fields/add-custom-fields-dialog";
import { AddFieldsDrawer } from "#app/components/issue-view/add-fields-drawer";
import { useYieldToEntityDetail } from "#app/components/entity";
import type { CustomFieldRow } from "#app/lib/data";
import { useCustomFieldsDialogActions } from "#app/lib/custom-fields-dialog-context";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useAvailableCustomFields } from "#app/hooks/issue-view/use-available-custom-fields";
import type { CustomFieldDef } from "#app/components/issue-view/issue-view-types";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";

/**
 * The custom fields attached to an entity, in type order: the ids the type
 * lists that carry a value, then any valued id the type no longer lists. This
 * is the one definition of "the section has fields" — the header controls, the
 * empty action and the module bodies all ask it, so they can never disagree.
 */
export function getAttachedFieldIds({
  values,
  orderedTypeFieldIds,
  alwaysShownFieldIds,
}: Omittable<{
  values?: Record<string, unknown>;
  orderedTypeFieldIds?: string[];
  /** Type fields that are on the summary even without a value record. */
  alwaysShownFieldIds?: string[];
}>) {
  const valueKeys = Object.keys(values ?? {}).filter((id) => id !== ".");
  if (!orderedTypeFieldIds?.length) return valueKeys;
  const valueKeySet = new Set([...valueKeys, ...(alwaysShownFieldIds ?? [])]);
  const ordered = orderedTypeFieldIds.filter((id) => valueKeySet.has(id));
  const orderedSet = new Set(ordered);
  return [...ordered, ...valueKeys.filter((id) => !orderedSet.has(id))];
}

export type SummaryAddCustomFieldsPickerProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The fields already on the entity — the picker offers the rest. */
  fieldIds: string[];
  customFields?: CustomFieldDef[];
  /** Adding a field writes its type default through this — the same door every
   *  row edit takes. */
  onUpdateValue?: (data: { customFieldId: string; value: unknown }) => void;
  /** Names the scope in the picker header. */
  locationName?: string;
}>;

/**
 * The "Add field from workspace" picker the Custom fields section opens — from
 * its header plus menu while it has fields, and from its empty action while it
 * has none. The window table picker on desktop; on a phone the shared
 * AddFieldsDrawer sheet (the drawer's Custom fields section opens the same
 * one). Picked fields land on the entity with their type default, and the
 * Create-field door inside routes to the workspace fields dialog for those
 * allowed to manage fields.
 */
export function SummaryAddCustomFieldsPicker({
  open,
  onOpenChange,
  fieldIds,
  customFields,
  onUpdateValue,
  locationName,
}: SummaryAddCustomFieldsPickerProps) {
  const { openDialog: openCustomFieldsDialog } = useCustomFieldsDialogActions();
  const canManageCustomFields = usePermission("custom_fields.manage");
  const isMobile = useIsMobile();
  const attachedIds = useMemo(() => new Set(fieldIds), [fieldIds]);
  // A Created By chip press in the desktop table opens the profile island,
  // which renders in the shell beneath this modal — the dialog yields.
  useYieldToEntityDetail(open && !isMobile, () => handleOpenChange(false));
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([]);
  // The picker's own query. Base UI's Autocomplete filters against `items`, and
  // this list is grouped + checkbox-rendered, so the filter stays explicit here
  // (`mode="none"`) over each field's name, key, type and type label.
  const [pickerQuery, setPickerQuery] = useState("");
  const customFieldsById = useMemo(() => {
    const map: Record<string, CustomFieldDef> = {};
    for (const field of customFields ?? []) {
      map[field.customFieldId] = field;
    }
    return map;
  }, [customFields]);
  const allAvailableFields = useAvailableCustomFields(fieldIds);
  const availableFields = useMemo(() => {
    const needle = pickerQuery.trim().toLowerCase();
    if (!needle) return allAvailableFields;
    return allAvailableFields.filter((field) =>
      [field.name, field.key ?? "", field.type, getFieldTypeMeta(field.type).label]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [allAvailableFields, pickerQuery]);

  // Closing — by any route — clears the draft selection and query, so the next
  // open starts clean.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      onOpenChange(next);
      if (!next) {
        setSelectedFieldIds([]);
        setPickerQuery("");
      }
    },
    [onOpenChange],
  );

  const handleNavigateToCreateField = useCallback(() => {
    handleOpenChange(false);
    openCustomFieldsDialog();
  }, [handleOpenChange, openCustomFieldsDialog]);

  const handleAddFields = useCallback(() => {
    for (const cfId of selectedFieldIds) {
      const defaultValue = getDefaultValueByType(customFieldsById[cfId]?.type);
      onUpdateValue?.({ customFieldId: cfId, value: defaultValue });
    }
    handleOpenChange(false);
  }, [selectedFieldIds, customFieldsById, onUpdateValue, handleOpenChange]);

  // The window picker hands whole rows back, so the type default needs no
  // field-bag lookup — the picked row carries its own type.
  const handleAddFieldRows = useCallback(
    (rows: CustomFieldRow[]) => {
      for (const row of rows) {
        onUpdateValue?.({
          customFieldId: row.customFieldId,
          value: getDefaultValueByType(row.type),
        });
      }
      handleOpenChange(false);
    },
    [onUpdateValue, handleOpenChange],
  );

  const toggleFieldSelection = useCallback((cfId: string) => {
    setSelectedFieldIds((prev) =>
      prev.includes(cfId) ? prev.filter((id) => id !== cfId) : [...prev, cfId],
    );
  }, []);

  if (isMobile) {
    return (
      <AddFieldsDrawer
        open={open}
        onOpenChange={handleOpenChange}
        fields={availableFields}
        selectedIds={selectedFieldIds}
        onToggle={toggleFieldSelection}
        canManage={canManageCustomFields}
        onManage={handleNavigateToCreateField}
        onAdd={handleAddFields}
        query={pickerQuery}
        onQueryChange={setPickerQuery}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPopup
        // bg-background over the stock bg-popover — the manager's body drop,
        // so the table reads on the page fill. (className = tracked gap.)
        className="bg-background"
        size="window"
        // No programmatic focus onto the toolbar's first control — Chrome
        // paints a :focus-visible ring for it. Focus stays trapped inside.
        initialFocus={false}
        showCloseButton={false}
      >
        <DialogTitle visuallyHidden>
          {locationName ? `Add fields to ${locationName}` : "Add fields"}
        </DialogTitle>
        <AddCustomFieldsContent
          attachedIds={attachedIds}
          onAdd={handleAddFieldRows}
          onCreateNew={canManageCustomFields ? handleNavigateToCreateField : undefined}
          onClose={() => handleOpenChange(false)}
          locationName={locationName}
        />
      </DialogPopup>
    </Dialog>
  );
}
